<script setup>
import BlockContactInfo from "~/components/wcag/block-contact-info.vue";
import BlockOrganization from "~/components/wcag/block-organization.vue";
import BlockDeclaration from "@/components/wcag/block-declaration.vue";
import BlockCompleteResearch from "@/components/wcag/block-complete-research.vue";
import BlockResearch from "@/components/wcag/block-research.vue";
import BlockFinish from "@/components/wcag/block-finish.vue";
import * as yup from "yup";

const props = defineProps({
  text: Object,
  textContact: Object,
  image: Object,
  link: Object,
});

const emit = defineEmits(["startScan"]);

const wcagStore = useWcagStore();
const progress = ref(0);
const isRunning = ref(false);
let interval = null;

const scanResults = ref(null);
const scanError = ref(null);

const url = ref("");
const email = ref("");
const errors = reactive({
  url: "",
  email: "",
});

const currentBlock = ref(1);
const blocks = {
  1: BlockContactInfo,
  2: BlockOrganization,
  3: BlockDeclaration,
  4: BlockCompleteResearch,
  5: BlockResearch,
  6: BlockFinish,
};

const activeBlock = computed(() => {
  return blocks[currentBlock.value] || BlockContactInfo;
});

// Define validation schema
const schema = yup.object({
  url: yup
    .string()
    .required("URL is verplicht")
    .url("Voer een geldige URL in (bijv. https://example.com)"),
  email: yup
    .string()
    .required("E-mail is verplicht")
    .email("Voer een geldig e-mailadres in"),
});

const validateForm = async () => {
  try {
    // Reset errors
    errors.url = "";
    errors.email = "";

    // Validate against schema
    await schema.validate(
      { url: url.value, email: email.value },
      { abortEarly: false }
    );
    return true;
  } catch (validationError) {
    // Handle validation errors
    if (validationError.inner) {
      validationError.inner.forEach((error) => {
        errors[error.path] = error.message;
      });
    }
    return false;
  }
};

const startProgressAnimation = () => {
  const incrementAmount = 1;
  const updateInterval = 100;

  interval = setInterval(() => {
    if (progress.value < 90) {
      progress.value += incrementAmount;
    } else {
      clearInterval(interval);
      interval = null;
    }
  }, updateInterval);
};

function resetStates() {
  progress.value = 0;
  isRunning.value = true;
  scanResults.value = null;
  scanError.value = null;
}

const goToNextBlock = async () => {
  if (currentBlock.value === 1) {
    const isValid = await validateForm();
    if (!isValid) return;
    else {
      wcagStore.setUrl(url.value);
      wcagStore.setEmail(email.value);
    }
  }

  // Increment the block
  if (currentBlock.value < Object.keys(blocks).length) {
    currentBlock.value++;
  }
};

// Handle moving to the previous block
const goToPreviousBlock = () => {
  if (currentBlock.value > 1) {
    currentBlock.value--;
  }
};

const goToFinalBlock = async () => {
  currentBlock.value = Object.keys(blocks).length;
  resetStates();
  startProgressAnimation();
  const startTime = Date.now();
  try {
    emit("startScan");
  } catch (error) {
    scanError.value = error.message;
    progress.value = 100;
  }
  const elapsedTime = Date.now() - startTime;
  const minimumTime = 10000; // 10 seconds
  if (elapsedTime < minimumTime) {
    await new Promise((resolve) =>
      setTimeout(resolve, minimumTime - elapsedTime)
    );
  }
  progress.value = 100;
  if (interval) {
    clearInterval(interval);
    interval = null;
  }
  isRunning.value = false;
};

// Update URL and email from block-1
const updateFormData = (newUrl, newEmail) => {
  url.value = newUrl;
  email.value = newEmail;
};

onMounted(() => {
  // Attach the scroll behavior to the button when the component is mounted
  const button = document.querySelector(".scroll-to-wcag-contact");
  if (button) {
    button.addEventListener("click", () => {
      const element = document.getElementById("wcag-contact");
      if (element) {
        element.scrollIntoView({ behavior: "smooth" });
      }
    });
  }
});
</script>

<template>
  <section id="wcag-contact" class="l-container p-section">
    <div class="l-row">
      <!-- WCAG -->
      <div class="p-col w-full md:w-1/2 mb-xl md:mb-0">
        <div
          class="p-block flex flex-col justify-between border-t-8 border-t-red bg-black-light border-gray-darker border-[1px] h-full"
        >
          <!-- Content WCAG block-->
          <component
            :is="activeBlock"
            :initial-url="url"
            :initial-email="email"
            :errors="errors"
            :progress="progress"
            :text="text"
            @next-block="goToNextBlock"
            @previous-block="goToPreviousBlock"
            @final-block="goToFinalBlock"
            @update-form="updateFormData"
          />

          <progress-bar v-if="isRunning || progress > 0" :progress="progress" />
        </div>
      </div>
      <!-- Contact -->
      <div class="p-col w-full md:w-1/2 mb-[90px] md:mb-0">
        <div
          class="relative flex flex-col items-center justify-center p-block border-t-8 border-t-white bg-black-light border-gray-darker border-[1px] h-full mt-3xl md:mt-0"
        >
          <!-- Image-->
          <div
            class="absolute flex items-center top-[-90px] h-[175px] w-[280px] xs:w-[375px] md:w-[300px] lg:w-[400px]"
          >
            <picture v-if="image">
              <source
                v-if="image.type !== 'svg' && image?.url?.webp"
                :srcset="image?.url?.webp"
                type="image/webp"
              />
              <source
                v-if="
                  image.type !== 'svg' &&
                  image.type !== 'png' &&
                  image?.url?.jpg
                "
                :srcset="image?.url?.jpg"
                type="image/jpeg"
              />
              <img
                v-if="image.type === 'jpg' && image?.url?.jpg"
                class="block rounded-full w-full"
                :src="image?.url?.jpg"
                :alt="image?.alt"
                :title="image?.title"
              />
              <img
                v-else-if="image.type === 'png' && image?.url?.png"
                class="block rounded-full w-full"
                :src="image?.url?.png"
                :alt="image?.alt"
                :title="image?.title"
              />
              <img
                v-else
                class="block rounded-full w-full"
                :src="image?.url?.original"
                :alt="image?.alt"
                :title="image?.title"
              />
            </picture>
          </div>
          <!-- Text + button -->
          <div v-if="textContact?.content" class="pt-[40px] md:pt-[80px] flex flex-grow">
            <div
              v-html="textContact?.content"
              class="prose flex flex-col justify-center"
            ></div>
          </div>
          <a
            v-if="link[0]"
            :href="link[0]?.url"
            :target="link[0]?.target"
            :aria-label="link[0]?.accessibilityText"
          >
            <button
              class="btn btn--white mt-lg w-full"
              :icon="true"
              icon-position="right"
            >
              {{ link[0]?.text }}
            </button>
          </a>
        </div>
      </div>
    </div>
  </section>
</template>

<style></style>
